import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { formatApiErrorDetail } from "@/lib/api";
import { Logo } from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, ShieldCheck } from "lucide-react";

const HERO =
  "https://images.unsplash.com/photo-1621394988863-117a9fc6e77f?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400";

export default function Login() {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (user && user.role) navigate(user.role === "agent" ? "/app" : "/dashboard", { replace: true });
  }, [user, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const u = await login(email.trim(), password);
      navigate(u.role === "agent" ? "/app" : "/dashboard", { replace: true });
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const quick = (em) => {
    setEmail(em);
    setPassword(em.includes("admin") ? "Admin@123" : "Agent@123");
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-background">
      {/* Hero */}
      <div className="relative hidden lg:flex flex-col justify-between p-12 overflow-hidden">
        <img src={HERO} alt="Basmati field" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-br from-black/80 via-black/55 to-emerald-950/70" />
        <div className="relative z-10">
          <Logo size={34} light />
        </div>
        <div className="relative z-10 max-w-md">
          <h1 className="text-white font-display text-4xl sm:text-5xl font-extrabold leading-[1.05] tracking-tight">
            Premium rice. <br /> Rewarding sales.
          </h1>
          <p className="text-white/70 mt-5 text-base leading-relaxed">
            One platform for the company to run prices, deposits and payouts — and for every
            agent to see exactly what they earn, in their own currency.
          </p>
        </div>
        <div className="relative z-10 flex items-center gap-2 text-white/60 text-sm">
          <ShieldCheck className="w-4 h-4" /> Bank-grade access. Agents only ever see their own data.
        </div>
      </div>

      {/* Form */}
      <div className="flex items-center justify-center p-6 sm:p-12">
        <div className="w-full max-w-sm fadeup">
          <div className="lg:hidden mb-8">
            <Logo size={32} />
          </div>
          <h2 className="font-display text-3xl font-bold tracking-tight">Sign in</h2>
          <p className="text-muted-foreground mt-2 text-sm">
            Enter your Orgaviya Foods credentials.
          </p>

          <form onSubmit={submit} className="mt-8 space-y-5">
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@orgaviya.com"
                required
                data-testid="login-email-input"
                className="h-12"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password">Password</Label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                required
                data-testid="login-password-input"
                className="h-12"
              />
            </div>
            {error && (
              <p className="text-sm text-destructive" data-testid="login-error">
                {error}
              </p>
            )}
            <Button
              type="submit"
              disabled={loading}
              className="w-full h-12 text-base"
              data-testid="login-submit-button"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Sign in"}
            </Button>
          </form>

          <div className="mt-8 border-t border-border pt-5">
            <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground mb-3">
              Demo accounts
            </p>
            <div className="grid grid-cols-1 gap-2">
              <button
                onClick={() => quick("admin@orgaviya.com")}
                className="text-left text-sm px-3 py-2 rounded-md border border-border hover:bg-secondary transition-colors"
                data-testid="demo-admin-button"
              >
                <span className="font-semibold">Admin</span> · admin@orgaviya.com
              </button>
              <button
                onClick={() => quick("rahul@orgaviya.com")}
                className="text-left text-sm px-3 py-2 rounded-md border border-border hover:bg-secondary transition-colors"
                data-testid="demo-agent-button"
              >
                <span className="font-semibold">Agent</span> · rahul@orgaviya.com (UAE)
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
